import {
  Button,
  Drawer,
  DrawerBody,
  DrawerFooter,
  DrawerHeader,
} from '@one-thd/sui-atomic-components';
import React, { useState, useEffect } from 'react';
import { DateCalendar } from '@one-thd/sui-date-pickers';
import { DateTime } from 'luxon';
import { parseDateToString } from '../../../utils/purchase-history/order-filter-utils';
import { DateFilterSettings, DateSettings } from '../../../models/order-filter';

interface CustomDateDrawerProps {
  setFilterSettings: React.Dispatch<React.SetStateAction<DateFilterSettings>>;
  filterSettings: DateFilterSettings;
  dateSettings: DateSettings;
  setDateSettings: React.Dispatch<React.SetStateAction<DateSettings>>;
}

export const CustomDateDrawer = ({
  setFilterSettings,
  filterSettings,
  dateSettings,
  setDateSettings,
}: CustomDateDrawerProps) => {
  const applyDateDisabled = () => {
    if (filterSettings.showStartCalendar) {
      return !dateSettings.startDate;
    }
    if (filterSettings.showEndCalendar) {
      return !dateSettings.endDate;
    }
    return false;
  };

  const { showStartCalendar, showEndCalendar } = filterSettings ?? {};

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  useEffect(() => {
    setIsDrawerOpen(showStartCalendar || showEndCalendar);
  }, [showStartCalendar, showEndCalendar]);

  const closeFilterDrawer = () => {
    setFilterSettings((prevState: DateFilterSettings) => ({
      ...prevState,
      showStartCalendar: false,
      showEndCalendar: false,
    }));
    setIsDrawerOpen(false);
  };

  if (!showStartCalendar && !showEndCalendar) {
    return null;
  }

  const maxDate = DateTime.now().startOf('day').toString();
  const minDate = DateTime.now()
    .minus({ months: 84 })
    .startOf('day')
    .toString();

  const toISODateValue = (
    date: string | null | undefined,
  ): string | undefined => {
    if (!date) return undefined;
    const parts = date.toString().split('/');
    if (parts.length === 3) {
      return `${parts[2]}-${parts[0]}-${parts[1]}`;
    }
    return date.toString();
  };

  const calendarValue = toISODateValue(
    filterSettings.showStartCalendar
      ? (dateSettings.startDate as string)
      : (dateSettings.endDate as string),
  );

  return (
    <Drawer
      open={isDrawerOpen}
      onClose={closeFilterDrawer}
      style={{ zIndex: 20000000011 }}
    >
      <DrawerHeader
        title="Custom Range"
        onBack={closeFilterDrawer}
        onClose={closeFilterDrawer}
      />
      <DrawerBody>
        <div>
          <DateCalendar
            minDate={minDate}
            maxDate={maxDate}
            value={calendarValue}
            onChange={(_event: unknown, value: unknown) => {
              if (value === null || value === undefined) return;

              const dt =
                value instanceof Date
                  ? DateTime.fromJSDate(value)
                  : DateTime.fromISO(value as string);

              if (!dt.isValid) return;

              const modifyDate = dt.toISODate();
              if (!modifyDate) return;

              setFilterSettings((prevState: DateFilterSettings) => ({
                ...prevState,
                customDateUsed: true,
              }));

              const newDate = parseDateToString(modifyDate);

              if (filterSettings.showStartCalendar) {
                setDateSettings((prevState: DateSettings) => ({
                  ...prevState,
                  startDate: newDate,
                  selectedDate: 'custom',
                }));
              } else if (filterSettings.showEndCalendar) {
                setDateSettings((prevState: DateSettings) => ({
                  ...prevState,
                  endDate: newDate,
                  selectedDate: 'custom',
                }));
              }
            }}
          />
        </div>
      </DrawerBody>
      <DrawerFooter>
        <Button
          variant="primary"
          fullWidth
          disabled={applyDateDisabled()}
          onClick={() => {
            closeFilterDrawer();
          }}
        >
          Apply
        </Button>
      </DrawerFooter>
    </Drawer>
  );
};
